<script lang="ts">
  import CurrentUserStore from "@shared/svelte/Stores/CurrentUserStore";
  import Flash from "@shared/services/flashv2.js";
  import { default as CraftNavigation } from "@modern/components/retailer/Navigation/ProductLinesNavigation.svelte";
  import order from "@shared/services/order";
  import PageTitle from "@shared/svelte/Components/PageTitle.svelte";
  import Pagination from "@shared/svelte/Components/Pagination/Large.svelte";
  import SortSelect from "@shared/svelte/Components/SortSelect.svelte";
  import * as _ from "lodash";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import {
    DistributorSortingEntity,
    DistributorEntity,
  } from "@shared/services/event_tracking/entities";

  import { Breadcrumbs } from "@getprovi/fizz-components";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import { get as getStoreValue } from "svelte/store";
  import { OnLayoutChange } from "@shared/svelte/Directives/OnLayoutChange.js";
  import type { Distributor, DistributorUser } from "@shared/types/Distributor";

  const { trackClick } = EventTracking;

  export let allDistributors: Distributor[] = [];

  interface BestSellers {
    [key: number]: string[];
  }

  interface OrderCounts {
    [key: number]: number;
  }

  let currentUser: DistributorUser = getStoreValue(CurrentUserStore) as DistributorUser;
  let isMobile: boolean = false;
  let currentPageDistributors: Distributor[] = [];
  let distributorSearchResults: Distributor[] = [];
  let searchInput: string;
  let totalEntries: number = 0;
  let pageNum: number = 1;
  let integratedOnly: boolean = false;
  let bestSellersLookup: BestSellers = {};
  let orderCountsLookup: OrderCounts = {};

  let sortBy: string = "popularity";

  const sortOptions = [
    { value: "popularity", text: "Popularity" },
    { value: "asc", text: "A to Z" },
    { value: "desc", text: "Z to A" },
  ];

  const isIntegratedDistributor = (distributor: Distributor): boolean => {
    return (
      distributor.integrated ||
      (distributor.seven_fifty_integration && distributor.uses_price_zones)
    );
  };
  const PER_PAGE: number = 15;
  const hasIntegratedDistributors: boolean =
    allDistributors.filter(isIntegratedDistributor).length > 0;

  $: pagination = {
    page: pageNum,
    perPage: PER_PAGE,
    totalEntries: totalEntries,
    goToPage: goToPage,
  };
  const updateIsMobile = name => {
    isMobile = name == "mobile";
  };
  const updateSort = async function () {
    trackClick(
      "all_distributors_page_sort_by_dropdown",
      new DistributorSortingEntity({ value: sortBy }),
    );
    pageNum = 1;
    searchDistributors();
  };
  const toggleIntegratedDistributors = async function () {
    trackClick("all_distributors_page_show_only_partner_distributors_checkbox");
    pageNum = 1;
    searchDistributors();
  };
  const paginate = function (distributors, page) {
    pageNum = page;
    return distributors.slice((pageNum - 1) * PER_PAGE, pageNum * PER_PAGE);
  };
  const goToPage = async function (page) {
    const distributors = paginate(distributorSearchResults, page);
    await fetchBestSellingBrandFamilies(distributors.map(d => d.id));
    currentPageDistributors = distributors;
  };
  const sortByPopularity = function (distributors) {
    return distributors.sort(function (a, b) {
      return (orderCountsLookup[b.id] || 0) - (orderCountsLookup[a.id] || 0);
    });
  };
  const setCurrentPage = function (distributors) {
    if (sortBy === "popularity") {
      distributorSearchResults = sortByPopularity(distributors);
    } else {
      distributorSearchResults = distributors;
    }
    const paginatedDistributors = paginate(distributorSearchResults, 1);
    fetchBestSellingBrandFamilies(paginatedDistributors.map(d => d.id));
    currentPageDistributors = paginatedDistributors;
    totalEntries = distributors.length;
  };

  const runSearch = _.debounce(searchDistributors, 400);
  async function searchDistributors() {
    await HttpPromise({
      method: "GET",
      url: "/api/retailer/distributors/search",
      data: {
        search: searchInput,
        sort_by: sortBy === "popularity" ? "" : sortBy,
        integrated_only: integratedOnly,
      },
    })
      .then(async res => {
        setCurrentPage(res);
      })
      .catch(res => {
        console.error(res);
        Flash.now({
          type: "error",
          header: "Error",
          message: "There was an error retrieving distributors.",
        });
      });
  }
  const fetchBestSellingBrandFamilies = async function (distributorIds: number[]) {
    await HttpPromise({
      method: "GET",
      url: "/api/retailer/distributors/best_selling_brand_families",
      data: { distributor_ids: distributorIds },
    })
      .then(res => {
        bestSellersLookup = res;
      })
      .catch(res => {
        console.error(res);
      });
  };
  const fetchOrderCountsAndSetInitialDistributors = async function () {
    await HttpPromise({
      method: "GET",
      url: "/api/retailer/distributors/order_counts",
    })
      .then(res => {
        orderCountsLookup = res;
      })
      .catch(res => {
        console.error(res);
        Flash.now({
          type: "error",
          header: "Error",
          message: "There was an error sorting by popularity.",
        });
      });
    setCurrentPage(allDistributors);
  };
  fetchOrderCountsAndSetInitialDistributors();
</script>

<PageTitle
  title={`All distributors in ${currentUser.market.name}, ${currentUser.market.state} | Provi`}
/>
<div use:OnLayoutChange={updateIsMobile}>
  <section>
    <CraftNavigation />
  </section>
  <div class="fizz-styles">
    <div class="fizz-surface-blue {isMobile ? 'fizz-inset-square-24' : 'fizz-inset-square-32'}">
      <div class="fizz-container">
        <div class="s4 fizz-stack-12">
          <Breadcrumbs links={[{ label: "Home", href: "/" }, { label: "All Distributors" }]} />
        </div>
        <h1 class="fizz-stack-8 {isMobile ? 'fizz-heading-3' : 'fizz-heading-1'}">
          All distributors in {currentUser.market.name}, {currentUser.market.state}
        </h1>
        <p class="fizz-text-large fizz-flex-row-8">
          Browse distributor catalogs on Provi to find the products you need and send orders to all
          of your sales reps
        </p>
      </div>
    </div>
    {#if hasIntegratedDistributors}
      <section
        class="background-white {isMobile ? 'fizz-inset-square-24' : 'fizz-inset-square-32'}"
      >
        <div class="fizz-flex-row fizz-stack-8 {!isMobile ? 'fizz-justify-center' : ''}">
          <img
            src={window["C"].IMAGE_PATHS["integrated.svg"]}
            class="fizz-inline-12"
            height="16"
            alt="integrated"
          />
          <div class={isMobile ? "fizz-text-large" : "fizz-heading-3"}>
            <strong>Our partner distributors</strong>
          </div>
        </div>
        <div class="fizz-flex-row {isMobile ? 'fizz-stack-8' : 'fizz-justify-center'}">
          <p>
            These distributors have partnered with Provi to offer additional product information and
            streamline the ordering process
          </p>
        </div>
      </section>
      <hr class="hr hr-card-separator--header" />
    {/if}
    <section
      class="background-white {isMobile ? 'fizz-inset-square-24' : 'fizz-inset-square-32'}"
      data-grid-area="main"
    >
      <div class="fizz-container">
        {#if isMobile}
          <div class="fizz-text-large"><strong>Browse all distributor catalogs</strong></div>
          <div class="fizz-text-small fizz-stack-8 fizz-text-subdued">
            {totalEntries}
            {totalEntries === 1 ? "distributor" : "distributors"}
          </div>
        {:else}
          <div class="fizz-flex-row fizz-stack-8">
            <div class="fizz-text-large"><strong>Browse all distributor catalogs</strong></div>
            <div class="fizz-text-small fizz-text-subdued">
              {totalEntries}
              {totalEntries === 1 ? "distributor" : "distributors"}
            </div>
          </div>
        {/if}
        <div class="fizz-stack-24 {!isMobile ? 'fizz-flex-row fizz-space-between' : ''}">
          <input
            name="distributor-search"
            class="fizz-input-search {isMobile
              ? 'fizz-full-width fizz-stack-8'
              : 'fizz-third-width'}"
            placeholder="Search distributor name"
            bind:value={searchInput}
            on:input={runSearch}
          />
          {#if hasIntegratedDistributors}
            <div class="fizz-flex-item fizz-hide-on-small">
              <label class="fizz-flex-row">
                <input
                  type="checkbox"
                  class="fizz-checkbox fizz-inline-8"
                  bind:checked={integratedOnly}
                  on:change={toggleIntegratedDistributors}
                />
                Show only partner distributors
              </label>
            </div>
          {/if}
          <div class="fizz-flex-row {isMobile ? 'fizz-stack-12' : 'fizz-inline-32'}">
            <SortSelect
              options={sortOptions}
              bind:selectedOptionValue={sortBy}
              onChange={updateSort}
            />
          </div>
          {#if hasIntegratedDistributors}
            <div class="fizz-hide-on-large">
              <label class="fizz-flex-row">
                <input
                  type="checkbox"
                  class="fizz-checkbox fizz-inline-8"
                  bind:checked={integratedOnly}
                  on:change={toggleIntegratedDistributors}
                />
                Show only partner distributors
              </label>
            </div>
          {/if}
        </div>
        <table
          class="fizz-table fizz-table-bordered"
          style={isMobile ? "border-bottom: 0px; border-top: 0px" : ""}
        >
          <tbody>
            {#each currentPageDistributors as distributor}
              <tr
                class="fizz-highlight-on-hover cursor-pointer"
                on:click={() => {
                  trackClick(
                    "all_distributors_page_view_catalog_link",
                    DistributorEntity.fromDistributor(distributor),
                  );
                  window["stateService"].go("layout.product_listing", {
                    distributors: distributor.id,
                  });
                }}
              >
                <td>
                  <div class={!isMobile ? "fizz-flex-row fizz-space-between" : ""}>
                    <div>
                      <div class="fizz-text-large fizz-stack-4 fizz-flex-row">
                        {#if isIntegratedDistributor(distributor)}
                          <img
                            src={window["C"].IMAGE_PATHS["integrated.svg"]}
                            class="fizz-inline-8"
                            height="12"
                            alt="integrated"
                          />
                        {/if}
                        <strong>{distributor.name}</strong>
                      </div>
                      {#if bestSellersLookup[distributor.id] && bestSellersLookup[distributor.id].length}
                        <div class="fizz-stack-4 fizz-flex-row fizz-text-subdued">
                          Sells: {bestSellersLookup[distributor.id].join(", ")}
                        </div>
                      {/if}
                    </div>
                    <div class="color-brandSecondary">View catalog</div>
                  </div>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
      <div class="margin-top--default fizz-styles align-center">
        <Pagination {pagination} />
      </div>
    </section>
  </div>
</div>
